(function () {
  // FRD: band <-> quantity matching, shared by the PDP element and the cart
  // reconciliation. Both sides have to agree on which band a quantity earns,
  // so the rules live in exactly one place.
  const FrdTiers = {
    // Accept any dash (hyphen, en/em dash) or "to" as the range separator, so a
    // band named "1–24" (macOS autocorrect) still yields an upper bound rather
    // than falling back to Infinity. A bare "10" is an open-ended band.
    parseRange(name) {
      const range = String(name).match(/(\d+)\s*(?:[-‐-―−]|to)?\s*(\d+)?/i);
      if (!range) return null;

      return {
        minimum: Number.parseInt(range[1], 10),
        maximum: range[2] ? Number.parseInt(range[2], 10) : Infinity
      };
    },

    isBand(variant) {
      if (!variant) return false;
      const option = String(variant.option1 || '').trim();
      if (!option || option.toLowerCase() === 'stock') return false;
      return /^\d/.test(option);
    },

    bands(variants) {
      return (variants || []).filter((variant) => FrdTiers.isBand(variant));
    },

    match(variants, quantity) {
      if (!Number.isFinite(quantity) || quantity < 1) return null;

      return FrdTiers.bands(variants).reduce((match, variant) => {
        const range = FrdTiers.parseRange(variant.option1);
        if (!range) return match;
        return quantity >= range.minimum && quantity <= range.maximum ? variant : match;
      }, null);
    },

    // How many of this variant can actually be bought right now. Untracked and
    // oversellable variants are unlimited; everything else is capped by stock,
    // which is the number the band price has to be earned against.
    maxPurchasable(variant) {
      if (!variant) return 0;
      if (!variant.inventoryManaged) return Infinity;
      if (variant.inventoryPolicy === 'continue') return Infinity;

      const quantity = Number(variant.inventoryQuantity);
      return Number.isFinite(quantity) ? Math.max(0, quantity) : 0;
    }
  };

  window.FrdTiers = window.FrdTiers || FrdTiers;

  if (customElements.get('frd-tiered-pricing')) return;

  class FrdTieredPricing extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const initialize = () => this.initialize();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialize, { once: true });
      } else {
        initialize();
      }
    }

    initialize() {
      this.productSection = this.closest('[data-section-type="product-template"]') ||
        document.getElementById(`ProductSection-${this.dataset.sectionId}`);
      this.form = document.getElementById(this.dataset.formId);

      const dataElement = this.querySelector('[data-frd-tier-data]');
      if (!this.productSection || !this.form || !dataElement) {
        this.initializationAttempts = (this.initializationAttempts || 0) + 1;
        if (this.initializationAttempts < 120) {
          window.requestAnimationFrame(() => this.initialize());
        }
        return;
      }

      try {
        this.data = JSON.parse(dataElement.textContent);
      } catch (error) {
        console.error('Unable to parse FRD tiered pricing data.', error);
        return;
      }

      this.variants = this.data.variants;
      this.variantIds = new Set(this.variants.map((variant) => String(variant.id)));
      this.stockVariant = this.variantById(this.dataset.stockVariantId) || this.variants[0];
      this.tierVariants = window.FrdTiers.bands(this.variants);

      // No ladder means nothing to enforce, and disabling Add to Cart against a
      // band that does not exist would strand the product. Leave the stock
      // buy flow alone.
      if (this.tierVariants.length === 0) return;

      this.quantityInput = this.productSection.querySelector('input[name="quantity"]');
      this.variantSelector = this.form.querySelector('[data-product-select]');
      this.weightInput = this.querySelector('[data-frd-tiered-weight]');
      this.priceElement = this.productSection.querySelector('[data-product-price]');
      this.addToCartButton = this.form.querySelector('[data-add-to-cart]');
      this.noticeElement = this.querySelector('[data-frd-tier-notice]');
      this.revealedPrices = new Set();
      this.mode = this.dataset.initialMode;

      this.productSection.classList.add('frd-tiered-pricing-active');
      if (this.hasAttribute('data-no-customization')) {
        this.productSection.classList.add('frd-tiered-pricing--no-customization');
      }
      this.bindEvents();
      this.setMode(this.mode, false);
      this.updateTier();
    }

    bindEvents() {
      this.querySelectorAll('input[name^="frd-product-type-"]').forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) this.setMode(input.value, true);
        });
      });

      if (this.quantityInput) {
        this.quantityInput.addEventListener('input', () => this.updateTier());
        this.quantityInput.addEventListener('change', () => this.updateTier());
      }

      this.querySelectorAll('[data-frd-click-for-price]').forEach((button) => {
        button.addEventListener('click', () => {
          const variantId = String(button.dataset.variantId);
          const hiddenPrice = document.getElementById(button.getAttribute('aria-controls'));
          this.revealedPrices.add(variantId);
          button.hidden = true;
          button.setAttribute('aria-expanded', 'true');
          if (hiddenPrice) hiddenPrice.hidden = false;
          this.updateTier();
        });
      });

      document.addEventListener('variant:change', (event) => {
        const variant = event.detail && event.detail.variant;
        if (!variant || !this.variantIds.has(String(variant.id))) return;

        const isStock = String(variant.id) === String(this.stockVariant.id);
        if (isStock && !this.hasAttribute('data-customized-only')) {
          this.setMode('blank', false);
        } else if (!isStock && !this.hasAttribute('data-blank-only')) {
          this.setMode('customized', false);
        }
      });
    }

    setMode(mode, updateVariant) {
      this.mode = mode;
      this.dataset.mode = mode;
      this.productSection.classList.toggle('frd-tiered-pricing--blank', mode === 'blank');
      this.productSection.classList.toggle('frd-tiered-pricing--customized', mode === 'customized');
      if (mode === 'blank' && this.priceElement) {
        this.priceElement.hidden = false;
        this.priceElement.classList.remove('hide');
      }

      const radio = this.querySelector(`input[value="${mode}"]`);
      if (radio) radio.checked = true;

      if (!updateVariant) return;
      if (mode === 'blank') {
        this.selectVariant(this.stockVariant);
        this.updateTier();
      } else {
        this.updateTier();
      }
    }

    updateTier() {
      const quantity = this.quantityInput ? Number.parseInt(this.quantityInput.value, 10) : 1;
      const tier = this.matchTier(quantity);

      this.querySelectorAll('[data-frd-tier-row]').forEach((row) => {
        row.classList.toggle('is-current', Boolean(tier) && row.dataset.variantId === String(tier.id));
      });

      if (!tier || !Number.isFinite(quantity) || quantity < 1) {
        if (this.addToCartButton) this.addToCartButton.disabled = true;
        this.weightInput.value = 0;
        // OLD blanked the price rather than leaving the previous tier's figure showing.
        if (this.priceElement) {
          this.priceElement.textContent = ' ';
          this.priceElement.hidden = false;
          this.priceElement.classList.remove('hide');
        }
        this.setNotice('');
        this.querySelectorAll('[data-frd-tier-row]').forEach((row) => row.classList.remove('is-current'));
        return;
      }

      if (this.mode === 'blank') {
        // Live 3.4.5 contract: _frd_tiered_weight is always quantity x the matched
        // tier's weight, even when the Stock variant is what gets added.
        this.weightInput.value = quantity * Number(tier.weight || 0);
        // Call-for-price gates the cart in BOTH modes on live: the tier is the thing
        // priced on application, regardless of which variant is being bought.
        this.productSection.classList.toggle('frd-tiered-pricing--call-for-price', Boolean(tier.callForPrice));
        // Blank sells the Stock variant, so that is the stock the quantity has
        // to fit inside.
        const blankShortfall = this.enforceStock(this.stockVariant, quantity);
        if (this.addToCartButton) {
          this.addToCartButton.disabled = Boolean(tier.callForPrice) ||
            this.stockVariant.available === false ||
            blankShortfall;
        }
        this.selectVariant(this.stockVariant);
        this.notifyDelivery(tier);
        return;
      }

      this.weightInput.value = quantity * Number(tier.weight || 0);
      this.selectVariant(tier);

      const priceIsHidden = tier.clickForPrice && !this.revealedPrices.has(String(tier.id));
      const cannotPurchase = tier.callForPrice;
      this.productSection.classList.toggle('frd-tiered-pricing--call-for-price', cannotPurchase);

      // The band price is only earned by a quantity that can actually be bought.
      // Shopify trims an over-quantity add to what is in stock and says nothing,
      // which used to leave (say) 50 pucks in the cart at the 1000-9999 price.
      const shortfall = this.enforceStock(tier, quantity);

      if (this.addToCartButton) {
        this.addToCartButton.disabled = cannotPurchase || tier.available === false || shortfall;
      }

      if (this.priceElement) {
        const hidePrice = priceIsHidden || cannotPurchase;
        this.priceElement.hidden = hidePrice;
        this.priceElement.classList.toggle('hide', hidePrice);
        if (!hidePrice) {
          this.priceElement.textContent = `${tier.priceFormatted} ${this.data.unitText}`;
        }
      }

      this.notifyDelivery(tier);
    }

    // Returns true when the quantity cannot be bought in full, after messaging
    // it. The blocked class also takes the dynamic checkout buttons out, because
    // Buy Now skips the cart entirely.
    enforceStock(variant, quantity) {
      const purchasable = window.FrdTiers.maxPurchasable(variant);
      const shortfall = quantity > purchasable;

      this.productSection.classList.toggle('frd-tiered-pricing--blocked', shortfall);

      if (!shortfall) {
        this.setNotice('');
        return false;
      }

      this.setNotice(
        purchasable > 0
          ? `Only ${purchasable} available. Lower the quantity to ${purchasable} or fewer to continue — the price per unit is set by the quantity you order. Contact us for a larger order.`
          : 'This quantity is not available to order online. Contact us for a bulk order.'
      );

      return true;
    }

    setNotice(message) {
      if (!this.noticeElement) return;
      this.noticeElement.textContent = message;
      this.noticeElement.hidden = message === '';
    }

    // Hand the matched tier's lead-time data to frd-delivery-estimates. Blank
    // metafields stay null so the delivery block's own defaults apply.
    notifyDelivery(tier) {
      const detail = {
        handlingDays: tier.handlingDays === '' ? null : tier.handlingDays,
        customizationDays: tier.customizationDays === '' ? null : tier.customizationDays,
        profile: tier.estimatedDeliveryProfile || null
      };

      if (window.frdDeliveryEstimates && typeof window.frdDeliveryEstimates.update === 'function') {
        window.frdDeliveryEstimates.update(detail);
      } else {
        document.dispatchEvent(new CustomEvent('frd:variant-change', { detail }));
      }
    }

    matchTier(quantity) {
      return window.FrdTiers.match(this.tierVariants, quantity);
    }

    selectVariant(variant) {
      if (!variant) return;
      if (this.variantSelector && this.variantSelector.value === String(variant.id)) return;

      let changedInput = null;
      variant.options.forEach((value, index) => {
        const inputs = this.productSection.querySelectorAll(`[data-variant-input][data-index="option${index + 1}"]`);
        inputs.forEach((input) => {
          if (input.value === value) {
            if (input.type === 'radio' || input.type === 'checkbox') input.checked = true;
            else input.value = value;
            changedInput = input;
          }
        });
      });

      if (changedInput) {
        changedInput.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      if (this.variantSelector) {
        this.variantSelector.value = String(variant.id);
        this.variantSelector.dispatchEvent(new Event('change', { bubbles: true }));
      }

      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: { variant }
      }));
    }

    variantById(id) {
      return this.variants.find((variant) => String(variant.id) === String(id));
    }
  }

  customElements.define('frd-tiered-pricing', FrdTieredPricing);
})();
